// src/components/PolicyCardNew.js
import React, { useState } from 'react';
import { Edit, Trash2, ChevronDown, ChevronUp, Shield, Lock, Ban } from 'lucide-react';
import Card from './Card';

const PolicyCardNew = ({ policy, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);

  if (!policy) {
    return null;
  }

  // Count tables by access type
  const getTableCounts = () => {
    const counts = {
      fully: 0,
      restricted: 0,
      forbidden: 0
    };

    if (policy.tables) {
      // Count fully allowed tables
      if (policy.tables.allowed_fully === '*') {
        counts.fully = 'All public';
      } else if (Array.isArray(policy.tables.allowed_fully)) {
        counts.fully = policy.tables.allowed_fully.length;
      }

      // Count restricted tables
      if (policy.tables.allowed_restricted) {
        counts.restricted = Object.keys(policy.tables.allowed_restricted).length;
      }

      // Count forbidden tables
      if (Array.isArray(policy.tables.forbidden)) {
        counts.forbidden = policy.tables.forbidden.length;
      }
    }

    return counts;
  };

  const counts = getTableCounts();

  return (
    <Card>
      <div className="p-4">
        {/* Header */}
        <div 
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-medium">{policy.name || 'Unnamed Policy'}</h3>
            <div className="flex space-x-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                className="text-gray-500 hover:text-gray-700"
                title="Edit policy"
              >
                <Edit className="h-5 w-5" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className="text-red-500 hover:text-red-700"
                title="Delete policy"
              >
                <Trash2 className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div className="text-gray-500">
            {expanded ? (
              <ChevronUp className="h-5 w-5" />
            ) : (
              <ChevronDown className="h-5 w-5" />
            )}
          </div>
        </div>

        {/* Summary Tags */}
        <div className="mt-3 flex flex-wrap gap-2">
          {counts.fully !== 0 && (
            <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <Shield className="h-3 w-3 mr-1" />
              {counts.fully === 'All public' ? 'All public tables' : `${counts.fully} full access`}
            </div>
          )}
          {counts.restricted > 0 && (
            <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              <Lock className="h-3 w-3 mr-1" />
              {counts.restricted} restricted
            </div>
          )}
          {counts.forbidden > 0 && (
            <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
              <Ban className="h-3 w-3 mr-1" />
              {counts.forbidden} forbidden
            </div>
          )}
          <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            CTE Wrapper
          </div>
        </div>

        {/* Expanded Content */}
        {expanded && policy.tables && (
          <div className="mt-4 space-y-4">
            {/* Fully Allowed Tables */}
            {(policy.tables.allowed_fully === '*' || policy.tables.allowed_fully?.length > 0) && (
              <div className="border border-green-200 rounded-md p-4 bg-green-50">
                <h4 className="font-medium text-green-900 mb-2">Full Access Tables</h4>
                {policy.tables.allowed_fully === '*' ? (
                  <div className="text-sm text-green-700">
                    <p>All public tables (excludes sensitive tables)</p>
                    {(Object.keys(policy.tables.allowed_restricted || {}).length > 0 || 
                      policy.tables.forbidden?.length > 0) && (
                      <div className="mt-2 pt-2 border-t border-green-300 text-xs">
                        <p className="font-medium text-green-900">With the following exceptions:</p>
                        {Object.keys(policy.tables.allowed_restricted || {}).length > 0 && (
                          <p className="text-blue-700 mt-1">
                            Restricted: {Object.keys(policy.tables.allowed_restricted).join(', ')}
                          </p>
                        )}
                        {policy.tables.forbidden?.length > 0 && (
                          <p className="text-red-700 mt-1">
                            No Access: {policy.tables.forbidden.join(', ')}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {policy.tables.allowed_fully.map(table => (
                      <span
                        key={table}
                        className="inline-flex items-center px-2 py-1 rounded text-xs bg-white text-green-800 border border-green-300"
                      >
                        {table}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Restricted Tables */}
            {policy.tables.allowed_restricted && Object.keys(policy.tables.allowed_restricted).length > 0 && (
              <div className="border border-blue-200 rounded-md p-4 bg-blue-50">
                <h4 className="font-medium text-blue-900 mb-2">Restricted Access Tables</h4>
                <div className="space-y-3">
                  {Object.entries(policy.tables.allowed_restricted).map(([tableName, config]) => (
                    <div key={tableName} className="bg-white rounded p-3 border border-blue-200">
                      <div className="font-medium text-blue-900">{tableName}</div>
                      <div className="mt-2 space-y-1 text-sm">
                        <div>
                          <span className="text-gray-600">Columns: </span>
                          <span className="text-blue-700">
                            {config.allowedColumns === '*' 
                              ? 'All columns' 
                              : Array.isArray(config.allowedColumns) 
                                ? config.allowedColumns.join(', ')
                                : 'None'}
                          </span>
                        </div>
                        {config.rowRestriction && (
                          <div>
                            <span className="text-gray-600">Row Filter: </span>
                            <code className="text-blue-700 bg-blue-100 px-1 rounded">
                              {config.rowRestriction}
                            </code>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Forbidden Tables */}
            {policy.tables.forbidden?.length > 0 && (
              <div className="border border-red-200 rounded-md p-4 bg-red-50">
                <h4 className="font-medium text-red-900 mb-2">Forbidden Tables</h4>
                <div className="flex flex-wrap gap-2">
                  {policy.tables.forbidden.map(table => (
                    <span
                      key={table}
                      className="inline-flex items-center px-2 py-1 rounded text-xs bg-white text-red-800 border border-red-300"
                    >
                      {table}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default PolicyCardNew;